import { useEffect } from 'react'
import { actions } from 'shared/store'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { DateSchema, NumberSchema, StringSchema, string, date } from 'yup'
import moment from 'moment'
import {
  hasObjectAnyEmptyValue,
  getValidationRules,
  REQUIRED_SPECIAL_CHARACTERS,
} from 'shared/utils'
import { DEFAULT_MAX_DATE, DEFAULT_MIN_DATE, PICKER_DATE_FORMAT } from 'config'

type RuleType =
  | 'email'
  | 'phoneNumber'
  | 'password'
  | 'contactName'
  | 'street'
  | 'postalCode'
  | 'businessRegistryNumber'
  | 'contractorName'
  | 'city'
  | 'buildingNumber'
  | 'apartmentNumber'
  | 'invoiceNumber'
  | 'amount'
  | 'date'
  | 'taxNumber'
  | 'fullName'
  | 'bankAccountNumber'
  | 'transferRecipient'
  | 'transferTitle'
  | 'actSignature'
  | 'message'

export type Field = {
  name: string
  rule: RuleType
  required?: boolean
}

export const useValidationRules = () => {
  const { t } = useTranslation()
  const baseRules = getValidationRules()
  const passwordError = t('errors:password.specialCharacters')
  const REQUIRED_KEY = 'errors:fillThisField'
  const basicValidation: Record<
    RuleType,
    DateSchema<Date | null> | NumberSchema | StringSchema
  > = {
    email: string()
      .matches(new RegExp(baseRules.emailRegex), t('errors:email.wrong'))
      .max(128, t('errors:email.length', { count: 128 })),
    amount: string()
      .matches(new RegExp(baseRules.amountRegex), t('errors:amount.wrong'))
      .test(
        'amount-greater-than-0',
        t('errors:amount.min', { count: 0 }),
        value => {
          return !value || parseFloat(value.replace(',', '.')) > 0
        }
      )
      .test(
        'amount-lower-than-10000000',
        t('errors:amount.max', { count: 10000000 }),
        value => {
          return !value || parseFloat(value.replace(',', '.')) < 10000000
        }
      ),
    phoneNumber: string()
      .min(3, t('errors:phoneNumber.length.min', { count: 3 }))
      .max(15, t('errors:phoneNumber.length.max', { count: 15 }))
      .matches(
        new RegExp(baseRules.phoneNumberRegex),
        t('errors:phoneNumber.wrong')
      ),
    apartmentNumber: string().max(
      10,
      t('errors:apartmentNumber.length', { count: 10 })
    ),
    transferTitle: string().max(
      140,
      t('errors:transferTitle.length.max', { count: 140 })
    ),
    transferRecipient: string().max(
      70,
      t('errors:transferRecipient.length.max', { count: 70 })
    ),
    buildingNumber: string().max(
      10,
      t('errors:buildingNumber.length', { count: 10 })
    ),
    businessRegistryNumber: string()
      .min(9, t('errors:businessRegistryNumber.length.min', { count: 9 }))
      .max(14, t('errors:businessRegistryNumber.length.max', { count: 14 }))
      .matches(
        new RegExp(baseRules.businessRegistryNumberRegex),
        t('errors:businessRegistryNumber.wrong')
      ),
    city: string().max(150, t('errors:city.length', { count: 150 })),
    contactName: string().max(
      50,
      t('errors:contactName.length', { count: 50 })
    ),
    fullName: string()
      .max(64, t('errors:fullName.length.max', { count: 64 }))
      .test(
        'has-minimum-length',
        t('errors:fullName.length.min', { count: 2 }),
        value => !value || value.trim().length >= 2
      ),
    contractorName: string().max(
      200,
      t('errors:contractorName.length', { count: 200 })
    ),
    invoiceNumber: string().test(
      'is-not-too-long',
      t('errors:invoiceNumber.length', { count: 50 }),
      value => {
        return !value || value.trim().length <= 50
      }
    ),
    password: string()
      .min(8, t('errors:password.length.min', { count: 8 }))
      .max(99, t('errors:password.length.max', { count: 99 }))
      .matches(new RegExp(/[a-z]/), passwordError)
      .matches(new RegExp(/[A-Z]/), passwordError)
      .test('contains-special-char', passwordError, value => {
        return (
          !value ||
          value
            .split('')
            .some(char => REQUIRED_SPECIAL_CHARACTERS.includes(char))
        )
      })
      .matches(new RegExp(/\d/), passwordError),
    postalCode: string().max(10, t('errors:postalCode.length', { count: 10 })),
    street: string().max(200, t('errors:street.length', { count: 200 })),
    date: date()
      .required(t('errors:fillThisField'))
      .typeError(t('errors:date.wrong'))
      .min(
        moment(DEFAULT_MIN_DATE, PICKER_DATE_FORMAT),
        t('errors:filters.dateShouldBeAfter', { date: DEFAULT_MIN_DATE })
      )
      .max(
        moment(DEFAULT_MAX_DATE, PICKER_DATE_FORMAT),
        t('errors:filters.dateShouldBeBefore', { date: DEFAULT_MAX_DATE })
      )
      .nullable()
      .default(null),
    taxNumber: string()
      .matches(
        new RegExp(baseRules.taxNumberRegex),
        t('errors:taxNumber.wrong')
      )
      .test(
        'is-correct-length',
        t('errors:taxNumber.wrong'),
        value => !value || value.length === 10
      )
      .test('is-checksum-correct', t('errors:taxNumber.wrong'), value => {
        if (!value) return true
        const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7]
        const controlNumber = parseInt(value[value.length - 1])
        const checkSum = weights.reduce(
          (sum, weight, idx) => sum + parseInt(value[idx]) * weight,
          0
        )
        return checkSum % 11 === controlNumber
      }),
    bankAccountNumber: string().test(
      'is-correct-length',
      t('errors:bankAccountNumber.length', { count: 40 }),
      value => !value || value.replace(/ /g, '').length <= 40
    ),
    actSignature: string()
      .test(
        'is-shorter-than-30',
        t('errors:actSignature.length.max', { count: 30 }),
        value => !value || value.trim().length <= 30
      )
      .test(
        'is-not-empty',
        t('errors:fillThisField'),
        value => !value || value.trim().length > 0
      ),
    message: string()
      .test(
        'is-not-empty',
        t('errors:fillThisField'),
        value => !value || value.trim().length > 0
      )
      .min(20, t('errors:chatMessage.length.min', { count: 20 }))
      .max(1000, t('errors:chatMessage.length.max', { count: 1000 })),
  }

  const getObjectShape = (fields: Field[]) => {
    return fields.reduce(
      (shape, { name, rule, required }) => ({
        ...shape,
        [name]: required
          ? basicValidation[rule].required(t(REQUIRED_KEY))
          : basicValidation[rule],
      }),
      {} as Record<
        string,
        DateSchema<Date | null> | NumberSchema | StringSchema
      >
    )
  }

  return { getObjectShape, basicValidation }
}

export const useValidation = () => {
  const dispatch = useDispatch()
  useEffect(() => {
    hasObjectAnyEmptyValue(getValidationRules()) &&
      dispatch(actions.settings.getValidation())
  }, [dispatch])
}
